// ============================================================================
// STUDIO CANETTE — FABRIQUE DE DOM
//
// Pas de framework : les pages construisent leur arbre à la main. Sans un
// hyperscript minimal, on retombe vite sur des `innerHTML` géants concaténés
// avec des données utilisateur — c'est-à-dire sur une faille XSS et sur du
// markup impossible à relire. `h()` produit de vrais nœuds, les enfants texte
// passent par `textContent`, et l'échappement n'est plus une question de
// discipline.
//
// Le seul endroit où l'on injecte du HTML brut est explicite : la propriété
// `html` de `h()` et la fonction `frag()`. Elles ne doivent JAMAIS recevoir
// autre chose qu'une chaîne écrite par nous (icônes SVG, gabarits statiques).
// Pour tout le reste : `text`, ou `esc()` si l'on n'a pas le choix.
// ============================================================================

/* ------------------------------------------------------------------ *
 * Échappement                                                         *
 * ------------------------------------------------------------------ */

const ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Rend une chaîne inoffensive dans un contexte HTML (texte ou attribut). */
export const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ENTITIES[c]);

/* ------------------------------------------------------------------ *
 * Sélection                                                           *
 * ------------------------------------------------------------------ */

/** Premier élément correspondant, ou null. */
export const el = (sel, root = document) => root.querySelector(sel);

/** Tous les éléments correspondants, en vrai tableau (donc .map, .filter…). */
export const els = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/** Vide un nœud et le renvoie, pour pouvoir enchaîner. */
export const clear = node => {
  if (node) while (node.firstChild) node.removeChild(node.firstChild);
  return node;
};

/* ------------------------------------------------------------------ *
 * Construction                                                        *
 * ------------------------------------------------------------------ */

// `div.card.acc-cyan#hero` : le nom de balise, puis les classes et l'id dans
// n'importe quel ordre. Écrire les classes structurelles ici évite un objet de
// props pour trois fois rien.
const TAG_RE = /^([a-zA-Z][\w-]*)?((?:[.#][\w-]+)*)$/;

function applyClass(node, v) {
  if (!v) return;
  if (Array.isArray(v)) { v.filter(Boolean).forEach(c => node.classList.add(...String(c).split(/\s+/).filter(Boolean))); return; }
  if (typeof v === 'object') { for (const [c, on_] of Object.entries(v)) if (on_) node.classList.add(c); return; }
  String(v).split(/\s+/).filter(Boolean).forEach(c => node.classList.add(c));
}

function applyStyle(node, v) {
  if (typeof v === 'string') { node.setAttribute('style', v); return; }
  for (const [k, val] of Object.entries(v || {})) {
    if (val === null || val === undefined || val === false) continue;
    // Les propriétés personnalisées (--v, --acc…) passent obligatoirement par
    // setProperty : `style['--v'] = …` est silencieusement ignoré.
    if (k.startsWith('--')) node.style.setProperty(k, String(val));
    else node.style[k] = val;
  }
}

function append(node, child) {
  if (child === null || child === undefined || child === false || child === true) return;
  if (Array.isArray(child)) { child.forEach(c => append(node, c)); return; }
  node.appendChild(child instanceof Node ? child : document.createTextNode(String(child)));
}

/**
 * Crée un élément.
 *
 * @param {string} tag     'div', 'button.btn.btn--primary', 'a#logo.sc-brand'
 * @param {Object} [props] class | style | dataset | text | html | on* | attributs
 * @param {...*}   children nœuds, chaînes, tableaux ; null/false sont ignorés
 * @returns {HTMLElement}
 */
export function h(tag, props, ...children) {
  const [, name = 'div', extra = ''] = String(tag).match(TAG_RE) || [];
  const node = document.createElement(name || 'div');

  for (const token of extra.match(/[.#][\w-]+/g) || []) {
    if (token[0] === '.') node.classList.add(token.slice(1));
    else node.id = token.slice(1);
  }

  // Le deuxième argument peut être un enfant : h('p', 'texte') doit marcher.
  if (props instanceof Node || Array.isArray(props) || typeof props === 'string' || typeof props === 'number') {
    children.unshift(props);
    props = null;
  }

  for (const [k, v] of Object.entries(props || {})) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class' || k === 'className') applyClass(node, v);
    else if (k === 'style') applyStyle(node, v);
    else if (k === 'dataset') Object.assign(node.dataset, v);
    else if (k === 'html') node.innerHTML = v;             // markup de confiance uniquement
    else if (k === 'text') node.textContent = v;
    else if (k === 'ref' && typeof v === 'function') v(node);
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2).toLowerCase(), v);
    // `value` et `checked` sont des propriétés vivantes : l'attribut ne fait que
    // définir la valeur par défaut, ce qui casse la reconstruction d'un champ.
    else if ((k === 'value' || k === 'checked' || k === 'indeterminate') && k in node) node[k] = v;
    else if (v === true) node.setAttribute(k, '');
    else node.setAttribute(k, String(v));
  }

  children.forEach(c => append(node, c));
  return node;
}

/**
 * Parse une chaîne HTML de confiance en DocumentFragment.
 * Passe par <template> : le contenu n'est pas exécuté, et les balises SVG y
 * sont créées dans le bon espace de noms (ce que `innerHTML` sur un div fait
 * aussi, mais pas `createElement('svg')`).
 */
export function frag(htmlString) {
  const t = document.createElement('template');
  t.innerHTML = String(htmlString ?? '').trim();
  return t.content;
}

/* ------------------------------------------------------------------ *
 * Événements                                                          *
 * ------------------------------------------------------------------ */

/**
 * Écoute un événement et renvoie la fonction de désabonnement.
 * Renvoyer le « off » plutôt que d'exiger la même référence de handler au
 * retrait est ce qui rend le nettoyage possible dans une page qui se démonte.
 *
 * @returns {() => void}
 */
export function on(target, type, handler, opts) {
  if (!target || !target.addEventListener) return () => {};
  target.addEventListener(type, handler, opts);
  return () => target.removeEventListener(type, handler, opts);
}

/* ------------------------------------------------------------------ *
 * Dates                                                               *
 * ------------------------------------------------------------------ */

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** « 12 mars 2026 ». Accepte un timestamp, une chaîne ISO ou une Date. */
export function fmtDate(ts) {
  if (ts === null || ts === undefined || ts === '') return '—';
  const d = ts instanceof Date ? ts : new Date(typeof ts === 'number' ? ts : String(ts));
  return Number.isNaN(d.getTime()) ? '—' : DATE_FMT.format(d);
}

/** « il y a 3 min », « hier », « le 12/03/2026 » — daté à la bonne échelle. */
export function timeAgo(ts) {
  if (!ts) return '—';
  const d = ts instanceof Date ? ts : new Date(typeof ts === 'number' ? ts : String(ts));
  const t = d.getTime();
  if (Number.isNaN(t)) return '—';
  const s = (Date.now() - t) / 1000;
  if (s < 0) return "à l'instant";
  if (s < 45) return "à l'instant";
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
  if (s < 172800) return 'hier';
  if (s < 2592000) return `il y a ${Math.round(s / 86400)} j`;
  return 'le ' + d.toLocaleDateString('fr-FR');
}
